import { useRef, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  RotateCcw,
  ScanText,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import CastingDraftTools from './CastingDraftTools';
import RoleCompensationFields from './RoleCompensationFields';
import CastingCallImageUpload from './CastingCallImageUpload';
import {
  APPLICATION_ROUTES,
  PAY_TYPES,
  PROJECT_TYPES,
} from '../../constants/castingCallOptions';

const API_URL = window.__BACKEND_URL__;
const AX = { withCredentials: true, timeout: 70000 };

const humanise = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isHttpUrl = (value) => {
  try {
    const url = new URL(String(value || '').trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const normaliseBoundedCount = (value, maximum) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isInteger(number) && number >= 1 && number <= maximum ? number : null;
};

const formatConfidence = (value) => (
  value == null || Number.isNaN(Number(value))
    ? 'Not scored'
    : `${Math.round(Number(value) * 100)}% confidence`
);

const emptyRole = () => ({
  role_name: '',
  gender: 'Any',
  age_min: '',
  age_max: '',
  role_type: '',
  accent_dialect: '',
  description: '',
  requirements: '',
});

const prepareEditableCall = (call = {}) => ({
  ...call,
  title: call.title || '',
  production_name: call.production_name || '',
  project_type: call.project_type || 'other',
  location: call.location || '',
  description: call.description || '',
  excerpt: call.excerpt || '',
  pay_type: call.pay_type || 'tbd',
  pay_amount: call.pay_amount || '',
  deadline: call.deadline || '',
  shoot_date: call.shoot_date || call.shoot_dates_from || '',
  shoot_dates_to: call.shoot_dates_to || '',
  additional_availability: call.additional_availability || '',
  application_route: call.application_route || 'internal',
  application_email: call.application_email || '',
  application_url: call.application_url || '',
  application_method: call.application_method || '',
  broadcast_platform: call.broadcast_platform || '',
  genre: Array.isArray(call.genre) && call.genre.some(Boolean)
    ? call.genre.filter(Boolean)
    : (call.vertical_details?.genre ? [call.vertical_details.genre] : []),
  vertical_details: {
    format: '9:16 Portrait / mobile-first',
    genre: call.vertical_details?.genre || (Array.isArray(call.genre) ? (call.genre[0] || '') : ''),
    platform: call.broadcast_platform || '',
    episode_count: '',
    episode_length: '1_3_minutes',
    shoot_days: '',
    language: 'English',
    territory: '',
    agreement: 'tbc',
    usage_terms: '',
    content_notes: '',
    ai_terms: '',
    ...(call.vertical_details || {}),
  },
  image_urls: Array.isArray(call.image_urls) ? call.image_urls : [],
  role_breakdowns: Array.isArray(call.role_breakdowns) && call.role_breakdowns.length
    ? call.role_breakdowns.map((role) => ({ ...emptyRole(), ...role }))
    : [emptyRole()],
});

function buildPublishPayload(call, fallbackFlyerUrl, attribution = {}) {
  const roles = Array.isArray(call?.role_breakdowns)
    ? call.role_breakdowns
      .map((role) => ({
        ...role,
        role_name: String(role?.role_name || '').trim(),
        age_min: role?.age_min === '' || role?.age_min == null ? null : Number(role.age_min),
        age_max: role?.age_max === '' || role?.age_max == null ? null : Number(role.age_max),
      }))
      .filter((role) => role.role_name)
    : [];

  const firstRole = roles[0] || {};
  const images = Array.from(new Set([
    ...(Array.isArray(call?.image_urls) ? call.image_urls : []),
    fallbackFlyerUrl,
  ].filter(Boolean))).slice(0, 5);

  return {
    title: String(call?.title || '').trim(),
    production_name: call?.production_name || null,
    project_type: call?.project_type || 'other',
    location: call?.location || null,
    description: call?.description || null,
    excerpt: call?.excerpt || null,
    role_name: firstRole.role_name || null,
    role_summary: firstRole.description || null,
    requirements: firstRole.requirements || null,
    gender_requirements: [firstRole.gender || 'Any'],
    age_range_min: firstRole.age_min ?? null,
    age_range_max: firstRole.age_max ?? null,
    pay_type: call?.pay_type || 'tbd',
    pay_amount: call?.pay_amount || null,
    deadline: call?.deadline || null,
    application_route: call?.application_route || 'internal',
    application_email: call?.application_route === 'email' ? (call?.application_email || null) : null,
    application_url: call?.application_route === 'external_url' ? (call?.application_url || null) : null,
    application_method: call?.application_method || null,
    attachments: [],
    flyer_url: images[0] || null,
    image_urls: images,
    genre: Array.isArray(call?.genre) ? call.genre.filter(Boolean) : (
      call?.vertical_details?.genre ? [call.vertical_details.genre] : []
    ),
    shoot_dates_from: call?.shoot_date || null,
    shoot_dates_to: call?.shoot_dates_to || null,
    shoot_date_from: call?.shoot_date || null,
    additional_availability: call?.additional_availability || null,
    broadcast_platform: call?.broadcast_platform || null,
    vertical_details: call?.project_type === 'vertical_drama'
      ? {
        ...(call?.vertical_details || {}),
        episode_count: normaliseBoundedCount(call?.vertical_details?.episode_count, 1000),
        shoot_days: normaliseBoundedCount(call?.vertical_details?.shoot_days, 365),
      }
      : {},
    application_deadline: call?.deadline || null,
    is_confidential: null,
    supporting_materials: [],
    role_breakdowns: roles,
    on_behalf_of_name: attribution.onBehalfOfName || null,
    poster_display_name: attribution.posterDisplayName || null,
  };
}

function TextField({ label, value, onChange, type = 'text', placeholder = '', required = false }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-theme-text">
        {label}{required ? ' *' : ''}
      </span>
      <input
        type={type}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full min-h-10 rounded-lg border border-theme bg-theme-bg-secondary px-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-theme-text">{label}</span>
      <select
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full min-h-10 rounded-lg border border-theme bg-theme-bg-secondary px-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      >
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? humanise(option) : option.label;
          return <option key={optionValue} value={optionValue}>{optionLabel}</option>;
        })}
      </select>
    </label>
  );
}

export default function CastFlowAIWorkspace({ mode = 'professional', onClose, onDraftDirtyChange }) {
  const allowNavigationRef = useRef(false);
  const isAdmin = mode === 'admin';
  const [sourceText, setSourceText] = useState('');
  const [flyerUrls, setFlyerUrls] = useState([]);
  const [onBehalfOfName, setOnBehalfOfName] = useState('');
  const [draft, setDraft] = useState(null);
  const [editableCall, setEditableCall] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [draftResetKey, setDraftResetKey] = useState(0);

  const reset = () => {
    setDraftResetKey((value) => value + 1);
    setSourceText('');
    setFlyerUrls([]);
    setDraft(null);
    setEditableCall(null);
    if (!isAdmin) setOnBehalfOfName('');
  };

  const analyse = async () => {
    if (!sourceText.trim() && flyerUrls.length === 0) {
      toast.error('Paste the casting text or upload a flyer first.');
      return;
    }

    setBusy(true);
    try {
      const endpoint = isAdmin
        ? '/api/admin/ai/casting/drafts'
        : '/api/casting-ai/drafts';

      const { data } = await axios.post(
        `${API_URL}${endpoint}`,
        {
          source_text: sourceText.trim(),
          flyer_url: flyerUrls[0] || null,
          flyer_urls: flyerUrls.slice(0, 5),
        },
        AX,
      );

      setDraft(data);
      setEditableCall(prepareEditableCall(data.casting_call));
      if (isAdmin) {
        setOnBehalfOfName(
          data.posting_on_behalf_of
          || data.on_behalf_of_name
          || data.casting_call?.production_name
          || ''
        );
      }

      data.missing_required?.length || data.duplicate_candidates?.length
        ? toast.warning('Draft created — review and fill any missing details.')
        : toast.success('CastFlow AI has built your editable casting draft.');
    } catch (error) {
      toast.error(String(
        error?.response?.data?.detail
        || 'CastFlow AI could not read this casting call.',
      ));
    } finally {
      setBusy(false);
    }
  };

  const setField = (field, value) => {
    setEditableCall((current) => ({ ...current, [field]: value }));
  };

  const setVerticalField = (field, value) => {
    setEditableCall((current) => ({
      ...current,
      vertical_details: {
        ...(current.vertical_details || {}),
        [field]: value,
      },
      ...(field === 'genre' ? { genre: value ? [value] : [] } : {}),
      ...(field === 'platform' ? { broadcast_platform: value } : {}),
    }));
  };

  const updateRole = (index, field, value) => {
    setEditableCall((current) => ({
      ...current,
      role_breakdowns: current.role_breakdowns.map((role, roleIndex) => (
        roleIndex === index ? { ...role, [field]: value } : role
      )),
    }));
  };

  const addRole = () => {
    setEditableCall((current) => ({
      ...current,
      role_breakdowns: [...current.role_breakdowns, emptyRole()],
    }));
  };

  const removeRole = (index) => {
    setEditableCall((current) => {
      const nextRoles = current.role_breakdowns.filter((_, roleIndex) => roleIndex !== index);
      return { ...current, role_breakdowns: nextRoles.length ? nextRoles : [emptyRole()] };
    });
  };

  const publish = async () => {
    const call = editableCall || {};
    const roles = Array.isArray(call.role_breakdowns) ? call.role_breakdowns : [];
    const hasNamedRole = roles.some((role) => String(role?.role_name || '').trim());

    if (!String(call.title || '').trim()) {
      toast.error('Add a title before publishing.');
      return;
    }
    if (!hasNamedRole) {
      toast.error('Add at least one named role before publishing.');
      return;
    }
    if (isAdmin && !String(onBehalfOfName || '').trim()) {
      toast.error('Add who CCUK is posting this casting call on behalf of.');
      return;
    }
    if (call.deadline && !ISO_DATE_PATTERN.test(String(call.deadline).trim())) {
      toast.error('Enter the application deadline as a complete date.');
      return;
    }
    if (call.shoot_date && call.shoot_dates_to && call.shoot_dates_to < call.shoot_date) {
      toast.error('Shoot end date must be the same as or later than the start date.');
      return;
    }
    if (call.project_type === 'vertical_drama') {
      const verticalCounts = [
        ['episode_count', 'Number of episodes', 1000],
        ['shoot_days', 'Expected shoot days', 365],
      ];
      for (const [field, label, maximum] of verticalCounts) {
        const value = call.vertical_details?.[field];
        if (value !== '' && value !== null && value !== undefined
          && normaliseBoundedCount(value, maximum) === null) {
          toast.error(`${label} must be a whole number from 1 to ${maximum}.`);
          return;
        }
      }
    }
    if (call.application_route === 'email' && !EMAIL_PATTERN.test(String(call.application_email || '').trim())) {
      toast.error('Enter a valid application email address.');
      return;
    }
    if (call.application_route === 'external_url' && !isHttpUrl(call.application_url)) {
      toast.error('Enter a complete http or https application link.');
      return;
    }
    if (draft?.duplicate_candidates?.length) {
      const proceed = window.confirm('A possible duplicate casting call exists. Publish this one anyway?');
      if (!proceed) return;
    }

    setPublishing(true);
    try {
      const payload = buildPublishPayload({
        ...call,
        image_urls: Array.from(new Set([
          ...(Array.isArray(call?.image_urls) ? call.image_urls : []),
          ...flyerUrls,
        ])).slice(0, 5),
      }, flyerUrls[0] || null, {
        onBehalfOfName: isAdmin ? String(onBehalfOfName).trim() : null,
        posterDisplayName: isAdmin ? null : (draft?.posting_identity || null),
      });

      const { data } = await axios.post(
        `${API_URL}/api/casting-calls`,
        payload,
        { withCredentials: true, timeout: 30000 },
      );

      allowNavigationRef.current = true;
      toast.success('Casting call published to the index.');
      window.location.assign(`/casting-calls/${data.id}`);
    } catch (error) {
      toast.error(String(
        error?.response?.data?.detail
        || 'Could not publish this casting call.',
      ));
    } finally {
      setPublishing(false);
    }
  };

  const call = editableCall || {};
  const roles = Array.isArray(call.role_breakdowns) ? call.role_breakdowns : [];
  const unresolved = [];
  if (!String(call.title || '').trim()) unresolved.push('Title');
  if (!roles.some((role) => String(role?.role_name || '').trim())) unresolved.push('At least one role name');
  if (isAdmin && !String(onBehalfOfName || '').trim()) unresolved.push('Posting on behalf of');
  const canPublish = Boolean(!unresolved.length && !publishing);

  const attributionText = isAdmin
    ? (String(onBehalfOfName || '').trim()
      ? `Posted by CCUK on behalf of ${String(onBehalfOfName).trim()}`
      : 'Posted by CCUK on behalf of…')
    : (draft?.posting_attribution || 'Posted by your active Casting Professional or Production Company profile');

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border border-theme bg-theme-surface shadow-sm" data-testid="castflow-ai-workspace">
      <div className="flex items-center gap-3 border-b border-theme px-4 py-3 sm:px-5">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-theme text-theme-muted hover:text-theme-text"
            aria-label={isAdmin ? 'Back to Admin AI' : 'Close CastFlow AI'}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#002FA7]/10 text-[#002FA7]">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-theme-text">CastFlow AI</div>
          <div className="text-[11px] text-theme-muted">Brief or flyer in. Editable casting call out.</div>
        </div>
        {draft && (
          <button
            type="button"
            onClick={reset}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-theme px-3 text-xs font-semibold text-theme-muted hover:text-[#002FA7]"
          >
            <RotateCcw className="h-3.5 w-3.5" /> New
          </button>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
        <CastingDraftTools editor="ai" allowNavigationRef={allowNavigationRef} resetKey={draftResetKey} onDirtyChange={onDraftDirtyChange}
          snapshot={{ sourceText, flyerUrls, onBehalfOfName, draft, editableCall }}
          title={editableCall?.title || 'CastFlow brief'}
          onRestore={(state) => {
            setSourceText(state.sourceText || ''); setFlyerUrls(state.flyerUrls || []);
            setOnBehalfOfName(state.onBehalfOfName || ''); setDraft(state.draft || null);
            setEditableCall(state.editableCall ? prepareEditableCall(state.editableCall) : null);
          }}
        />
        {!draft ? (
          <>
            <div className="rounded-2xl border border-[#002FA7]/20 bg-[#002FA7]/[0.05] p-4">
              <div className="font-semibold text-theme-text">The quick way to post a proper casting call</div>
              <p className="mt-1 text-sm leading-relaxed text-theme-muted">
                Paste an email, WhatsApp message, casting brief or social post and add the flyer if you have one. CastFlow AI reads both, structures the roles, dates, pay, application details and index-card excerpt, then gives you an editable draft. Nothing goes live until you check it and press Publish.
              </p>
            </div>

            {isAdmin && (
              <div className="rounded-2xl border border-violet-500/20 bg-violet-500/[0.05] p-4" data-testid="castflow-admin-attribution">
                <TextField
                  label="CCUK is posting on behalf of"
                  value={onBehalfOfName}
                  onChange={setOnBehalfOfName}
                  placeholder="e.g. North Star Casting or Riverbank Productions"
                  required
                />
                <p className="mt-2 text-xs text-theme-muted">
                  Public attribution will read: <strong>{attributionText}</strong>
                </p>
                <p className="mt-1 text-xs text-theme-muted">
                  CastFlow AI will also suggest this from the extracted production/company name.
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-semibold text-theme-text" htmlFor="castflow-source">Casting brief</label>
              <textarea
                id="castflow-source"
                value={sourceText}
                onChange={(event) => setSourceText(event.target.value)}
                rows={8}
                maxLength={12000}
                placeholder="Paste the casting brief, email, WhatsApp message or social post here…"
                className="mt-1.5 w-full rounded-xl border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
              />
              <div className="mt-1 text-right text-[10px] text-theme-muted">{sourceText.length} / 12,000</div>
            </div>

            <div className="rounded-2xl border border-theme bg-theme-bg-secondary/35 p-4">
              <CastingCallImageUpload
                values={flyerUrls}
                onChange={setFlyerUrls}
              />
            </div>

            <button
              type="button"
              onClick={analyse}
              disabled={busy || (!sourceText.trim() && flyerUrls.length === 0)}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#002FA7] px-4 text-sm font-bold text-white hover:bg-[#0055FF] disabled:opacity-40"
              data-testid="castflow-extract-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanText className="h-4 w-4" />}
              {busy ? 'CastFlow AI is reading it…' : 'Create my casting draft'}
            </button>

            <div className="flex items-start gap-2 rounded-xl bg-theme-bg-secondary/50 px-3 py-2.5 text-xs leading-relaxed text-theme-muted">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
              You stay in control. AI never publishes automatically and the normal CCUK posting permissions still apply.
            </div>
          </>
        ) : (
          <>
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-500" />
                <div>
                  <div className="font-bold text-theme-text">Draft ready — give it a quick check</div>
                  <div className="mt-0.5 text-xs text-theme-muted">{formatConfidence(draft.overall_confidence)} · every field below is editable</div>
                </div>
              </div>
              <div className="mt-3 rounded-xl bg-theme-surface/70 px-3 py-2 text-xs font-semibold text-theme-text" data-testid="castflow-posting-attribution">
                {attributionText}
              </div>
            </div>

            {!!draft.warnings?.length && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3">
                <div className="mb-1 font-bold text-amber-600">AI notes</div>
                <div className="space-y-1 text-xs text-theme-muted">
                  {draft.warnings.map((warning) => <div key={warning}>• {warning}</div>)}
                </div>
              </div>
            )}

            {!!unresolved.length && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3">
                <div className="flex items-center gap-1.5 text-xs font-bold text-amber-600">
                  <AlertTriangle className="h-4 w-4" /> Missing before publishing
                </div>
                <div className="mt-1.5 text-xs text-theme-muted">{unresolved.join(' · ')}</div>
              </div>
            )}

            {isAdmin && (
              <div className="rounded-2xl border border-violet-500/20 bg-violet-500/[0.05] p-4">
                <TextField
                  label="CCUK is posting on behalf of"
                  value={onBehalfOfName}
                  onChange={setOnBehalfOfName}
                  required
                />
              </div>
            )}

            <section className="space-y-3 rounded-2xl border border-theme p-4" data-testid="castflow-edit-form">
              <h3 className="font-bold text-theme-text">Listing details</h3>
              <TextField label="Title" value={call.title} onChange={(value) => setField('title', value)} required />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField label="Production / company" value={call.production_name} onChange={(value) => setField('production_name', value)} />
                <SelectField label="Project type" value={call.project_type} onChange={(value) => setField('project_type', value)} options={PROJECT_TYPES} />
                <TextField label="Location" value={call.location} onChange={(value) => setField('location', value)} />
                <TextField label="Broadcaster / platform" value={call.broadcast_platform} onChange={(value) => setField('broadcast_platform', value)} />
              </div>
              <label className="block">
                <span className="text-[11px] font-semibold text-theme-text">Description</span>
                <textarea value={call.description || ''} onChange={(event) => setField('description', event.target.value)} rows={4} className="mt-1 w-full rounded-lg border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y" />
              </label>
              <label className="block" data-testid="castflow-index-excerpt-field">
                <span className="text-[11px] font-semibold text-theme-text">Index card excerpt</span>
                <textarea
                  value={call.excerpt || ''}
                  onChange={(event) => setField('excerpt', event.target.value)}
                  rows={2}
                  maxLength={190}
                  placeholder="Short public summary shown on the casting calls index card"
                  className="mt-1 w-full rounded-lg border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
                />
                <span className="mt-1 block text-[11px] text-theme-muted">
                  Keep this short. It appears on the public index card before members open the full casting call.
                </span>
              </label>
              <TextField label="Genre (comma separated)" value={(call.genre || []).join(', ')} onChange={(value) => setField('genre', value.split(',').map((item) => item.trim()).filter(Boolean))} />
            </section>

            {call.project_type === 'vertical_drama' && (
              <section className="space-y-3 rounded-2xl border border-[#002FA7]/25 bg-[#002FA7]/[0.03] p-4" data-testid="castflow-vertical-details">
                <h3 className="font-bold text-theme-text">Vertical production details</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <TextField label="Format" value={call.vertical_details?.format} onChange={(value) => setVerticalField('format', value)} />
                  <TextField label="Primary genre" value={call.vertical_details?.genre} onChange={(value) => setVerticalField('genre', value)} />
                  <TextField label="Platform / distributor" value={call.vertical_details?.platform} onChange={(value) => setVerticalField('platform', value)} />
                  <TextField label="Number of episodes" type="number" value={call.vertical_details?.episode_count} onChange={(value) => setVerticalField('episode_count', value)} />
                  <SelectField label="Approx. episode length" value={call.vertical_details?.episode_length} onChange={(value) => setVerticalField('episode_length', value)} options={['under_1_minute', '1_3_minutes', '3_5_minutes', 'over_5_minutes', 'tbc']} />
                  <TextField label="Expected shoot days" type="number" value={call.vertical_details?.shoot_days} onChange={(value) => setVerticalField('shoot_days', value)} />
                  <TextField label="Language" value={call.vertical_details?.language} onChange={(value) => setVerticalField('language', value)} />
                  <TextField label="Distribution / territory" value={call.vertical_details?.territory} onChange={(value) => setVerticalField('territory', value)} />
                </div>
                <SelectField label="Agreement / union status" value={call.vertical_details?.agreement} onChange={(value) => setVerticalField('agreement', value)} options={['tbc', 'non_union', 'equity_uk', 'sag_aftra_verticals', 'other']} />
                <label className="block">
                  <span className="text-[11px] font-semibold text-theme-text">Usage / buyout terms</span>
                  <textarea value={call.vertical_details?.usage_terms || ''} onChange={(event) => setVerticalField('usage_terms', event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold text-theme-text">Content / intimacy notes</span>
                  <textarea value={call.vertical_details?.content_notes || ''} onChange={(event) => setVerticalField('content_notes', event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold text-theme-text">AI / digital replica terms</span>
                  <textarea value={call.vertical_details?.ai_terms || ''} onChange={(event) => setVerticalField('ai_terms', event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y" />
                </label>
              </section>
            )}

            <section className="space-y-3 rounded-2xl border border-theme p-4">
              <h3 className="font-bold text-theme-text">Pay & dates</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <SelectField label="Pay type" value={call.pay_type} onChange={(value) => setField('pay_type', value)} options={PAY_TYPES} />
                <TextField label="Pay amount / rate" value={call.pay_amount} onChange={(value) => setField('pay_amount', value)} placeholder="e.g. £350 per day" />
                <TextField label="Application deadline" type="date" value={call.deadline} onChange={(value) => setField('deadline', value)} />
                <TextField label="Shoot / work start" type="date" value={call.shoot_date} onChange={(value) => setField('shoot_date', value)} />
                <TextField label="Shoot / work end" type="date" value={call.shoot_dates_to} onChange={(value) => setField('shoot_dates_to', value)} />
              </div>
              <TextField label="Additional availability / date notes" value={call.additional_availability} onChange={(value) => setField('additional_availability', value)} />
            </section>

            <section className="space-y-3 rounded-2xl border border-theme p-4">
              <h3 className="font-bold text-theme-text">How to apply</h3>
              <SelectField label="Application route" value={call.application_route} onChange={(value) => setField('application_route', value)} options={APPLICATION_ROUTES} />
              {call.application_route === 'email' && (
                <TextField label="Application email" type="email" value={call.application_email} onChange={(value) => setField('application_email', value)} />
              )}
              {call.application_route === 'external_url' && (
                <TextField label="Application URL" type="url" value={call.application_url} onChange={(value) => setField('application_url', value)} />
              )}
              <TextField label="Application instructions" value={call.application_method} onChange={(value) => setField('application_method', value)} />
            </section>

            <section className="space-y-3 rounded-2xl border border-theme p-4" data-testid="castflow-role-editor">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-bold text-theme-text">Roles</h3>
                <button type="button" onClick={addRole} className="flex min-h-9 items-center gap-1 rounded-lg border border-theme px-3 text-xs font-semibold text-[#002FA7]">
                  <Plus className="h-3.5 w-3.5" /> Add role
                </button>
              </div>

              {roles.map((role, index) => (
                <div key={`role-${index}`} className="space-y-3 rounded-xl border border-theme bg-theme-bg-secondary/30 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold text-theme-text">Role {index + 1}</div>
                    <button type="button" onClick={() => removeRole(index)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-theme text-theme-muted hover:text-rose-500" aria-label={`Remove role ${index + 1}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <TextField label="Role name" value={role.role_name} onChange={(value) => updateRole(index, 'role_name', value)} required />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <SelectField label="Gender" value={role.gender || 'Any'} onChange={(value) => updateRole(index, 'gender', value)} options={['Any', 'Male', 'Female', 'Non-binary']} />
                    <TextField label="Role type" value={role.role_type} onChange={(value) => updateRole(index, 'role_type', value)} placeholder="Lead, Supporting, Background…" />
                    <TextField label="Minimum age" type="number" value={role.age_min} onChange={(value) => updateRole(index, 'age_min', value)} />
                    <TextField label="Maximum age" type="number" value={role.age_max} onChange={(value) => updateRole(index, 'age_max', value)} />
                    <TextField label="Accent / dialect" value={role.accent_dialect} onChange={(value) => updateRole(index, 'accent_dialect', value)} />
                  </div>
                  <RoleCompensationFields role={role} casting={call} onChange={(field, value) => updateRole(index, field, value)} />
                  <label className="block">
                    <span className="text-[11px] font-semibold text-theme-text">Role description</span>
                    <textarea value={role.description || ''} onChange={(event) => updateRole(index, 'description', event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-semibold text-theme-text">Requirements</span>
                    <textarea value={role.requirements || ''} onChange={(event) => updateRole(index, 'requirements', event.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-theme bg-theme-bg-secondary p-3 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y" />
                  </label>
                </div>
              ))}
            </section>

            {!!draft.duplicate_candidates?.length && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.05] p-3 text-xs text-theme-muted">
                <div className="mb-1 font-bold text-rose-500">Possible duplicate</div>
                A listing with the same title already exists. Publishing will ask you to confirm.
              </div>
            )}

            <button
              type="button"
              onClick={publish}
              disabled={!canPublish}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#002FA7] px-4 py-3 text-sm font-bold text-white hover:bg-[#0055FF] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="castflow-publish-button"
            >
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {publishing ? 'Publishing…' : 'Publish reviewed casting call'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export { buildPublishPayload };
